import {
  Button,
  FilterOutlined,
  Space,
  Tooltip,
  Typography,
  getPaginationConfig,
} from "@mimber/ui-web"
import type { QueryParamsDto } from "@mimber/schema"

const { Text } = Typography

const hasActiveQueryState = (queryParams?: QueryParamsDto): boolean => {
  if (!queryParams) {
    return false
  }

  const hasSearch =
    typeof queryParams.search === "string" && queryParams.search.trim() !== ""
  const hasSort = Boolean(
    queryParams.sort && Object.keys(queryParams.sort).length
  )
  const hasFilters = Boolean(
    queryParams.filters && Object.keys(queryParams.filters).length
  )

  return hasSearch || hasSort || hasFilters
}

export const getRememberedPaginationConfig = (
  total: number,
  queryParams: QueryParamsDto,
  onReset?: () => void,
  entityName: string = "items"
) => {
  const baseConfig = getPaginationConfig(total, queryParams, entityName)

  if (!onReset) {
    return baseConfig
  }

  return {
    ...baseConfig,
    showTotal: (totalCount: number, range: [number, number]) => (
      <Space size={8}>
        <Tooltip title="Clear table filters and sorting">
          <Button
            aria-label="Clear table filters and sorting"
            type="text"
            size="small"
            icon={<FilterOutlined />}
            onClick={onReset}
          />
        </Tooltip>
        <span>{`${range[0]}-${range[1]} of ${totalCount} ${entityName}`}</span>
      </Space>
    ),
  }
}

export const getRememberedTableLocale = (
  onReset?: () => void,
  queryParams?: QueryParamsDto
) => {
  if (!onReset) {
    return undefined
  }

  if (!hasActiveQueryState(queryParams)) {
    return undefined
  }

  return {
    emptyText: (
      <Space direction="vertical" size={8} style={{ textAlign: "center" }}>
        <Text type="secondary">No results match the current filters.</Text>
        <Button type="link" size="small" onClick={onReset}>
          Reset filters
        </Button>
      </Space>
    ),
  }
}

/**
 * Extra action for an error Result state that lets the user clear stale/invalid
 * remembered filters (e.g. a filter field removed from the allowed list) instead
 * of getting stuck on a permanent error.
 */
export const getRememberedTableErrorAction = (
  onReset?: () => void,
  queryParams?: QueryParamsDto
) => {
  if (!onReset || !hasActiveQueryState(queryParams)) {
    return undefined
  }

  return <Button onClick={onReset}>Clear filters</Button>
}
